import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { formatUserName, getEmergencyAdminWarning } from '../../utils/userDisplayUtils';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button
} from '@mui/material';
import {
  GolfCourse,
  People,
  PlayArrow,
  TrendingUp
} from '@mui/icons-material';

export default function Dashboard() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalTournaments: 0,
    activeTournaments: 0,
    totalPlayers: 0,
    completedTournaments: 0
  });

  useEffect(() => {
    fetchStats();
  }, [userProfile?.organizationName]);

  const fetchStats = async () => {
    try {
      // Fetch tournaments
      const tournamentsQuery = query(
        collection(db, 'tournaments'),
        where('organizationId', '==', userProfile?.organizationName)
      );
      const tournamentsSnapshot = await getDocs(tournamentsQuery);
      
      const tournaments = tournamentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Fetch players
      const playersQuery = query(
        collection(db, 'players'),
        where('organizationId', '==', userProfile?.organizationName)
      );
      const playersSnapshot = await getDocs(playersQuery);

      setStats({
        totalTournaments: tournaments.length,
        activeTournaments: tournaments.filter(t => t.status === 'active').length,
        totalPlayers: playersSnapshot.size,
        completedTournaments: tournaments.filter(t => t.status === 'completed').length
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const StatCard = ({ title, value, icon, color = 'primary' }) => (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" component="h2">
              {value}
            </Typography>
          </Box>
          <Box color={`${color}.main`}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {getEmergencyAdminWarning(userProfile)}
      <Typography variant="h4" gutterBottom>
        Welcome back, {userProfile?.firstName}!
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        {userProfile?.organizationName} - Tournament Management Dashboard
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Tournaments"
            value={stats.totalTournaments}
            icon={<GolfCourse sx={{ fontSize: 40 }} />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Tournaments"
            value={stats.activeTournaments}
            icon={<PlayArrow sx={{ fontSize: 40 }} />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Players"
            value={stats.totalPlayers}
            icon={<People sx={{ fontSize: 40 }} />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Completed"
            value={stats.completedTournaments}
            icon={<TrendingUp sx={{ fontSize: 40 }} />}
            color="secondary"
          />
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<GolfCourse />}
                onClick={() => navigate('/tournaments', { state: { openCreateDialog: true } })}
              >
                Create New Tournament
              </Button>
              <Button
                variant="outlined"
                startIcon={<People />}
                onClick={() => navigate('/players')}
              >
                Manage Players
              </Button>
              <Button
                variant="outlined"
                startIcon={<TrendingUp />}
                onClick={() => navigate('/tournaments')}
              >
                View All Tournaments
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>
            <Typography color="text.secondary">
              No recent activity to display.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
